<template>
  <div
    class="draw-card"
    :class="{ horizontal: layout === 'horizontal' }"
    @click="handleCardClick"
    tabindex="0"
    role="button"
  >
    <div class="card-content" @click.stop="">
      <template v-if="layout === 'horizontal'">
        <button
          class="favorite-btn horizontal-favorite"
          :class="{ active: isFavoritedProp }"
          @click.stop="toggleFavorite"
          :aria-label="$t('forms.toggleFavorite')"
        >
          <div class="favorite-icon-wrapper">
            <img :src="isFavoritedProp ? favoriteFilledIcon : favoriteIcon" :alt="$t('forms.favorite')" class="favorite-icon" :class="{ 'active-favorite-icon': isFavoritedProp }" />
          </div>
        </button>
        <div class="left-section" @click="navigateToDetail">
          <div class="flag-container">
            <img :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)" :alt="`${lottery.country} ${$t('forms.flag')}`" class="flag" />
          </div>
          <h3 class="lottery-name">{{ lottery.game_name }}</h3>
        </div>
        <div class="right-section" @click="navigateToDetail">
          <div class="countdown">
            <div class="time-block">
              <div class="time">{{ String(countdown.hours).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.hours') }}</div>
            </div>
            <div class="time-block">
              <div class="time">{{ String(countdown.minutes).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.mins') }}</div>
            </div>
            <div class="time-block">
              <div class="time">{{ String(countdown.seconds).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.secs') }}</div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="card-header" @click="navigateToDetail">
          <h3 class="country-name">{{ lottery.game_name }}</h3>
          <button class="favorite-btn" :class="{ active: isFavoritedProp }" @click.stop="toggleFavorite">
            <div class="favorite-icon-wrapper">
            <img :src="isFavoritedProp ? favoriteFilledIcon : favoriteIcon" :alt="$t('forms.favorite')" class="favorite-icon" />
          </div>
          </button>
        </div>
        <div class="flag-container" @click="navigateToDetail">
          <img :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)" :alt="`${lottery.country} ${$t('forms.flag')}`" class="flag" />
        </div>
        <div class="draw-info">
          <div class="countdown">
            <div class="time-block">
              <div class="time">{{ String(countdown.hours).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.hours') }}</div>
            </div>
            <div class="time-block">
              <div class="time">{{ String(countdown.minutes).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.mins') }}</div>
            </div>
            <div class="time-block">
              <div class="time">{{ String(countdown.seconds).padStart(2, '0') }}</div>
              <div class="label">{{ $t('time.secs') }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <button class="bet-button" v-if="!betClosed" @click.stop="handleBet">{{ $t('common.betNow') }}</button>
    <button class="bet-button" v-else disabled>{{ $t('common.betClosed') }}</button>
  </div>
</template>


<script lang="ts">
export default {
  name: 'LotteryCard'
}
</script>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, defineProps, defineEmits, defineOptions, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getGameLogoUrl } from '@/utils/resultParser';
import { useTheme } from '@/composables/useTheme';
import type { UpcomingDraw } from '@/types/lottery';

const { getAssetPath } = useTheme();
const favoriteIcon = getAssetPath('icons', 'favorite') || '/img/icons/favorite.svg';
const favoriteFilledIcon = getAssetPath('icons', 'favorite-filled') || '/img/icons/favorite-filled.svg';
const router = useRouter()

defineOptions({
  name: 'LotteryCard'
})

const props = defineProps<{
  lottery: UpcomingDraw;
  layout?: 'vertical' | 'horizontal';
  isFavorited?: (draw: UpcomingDraw) => boolean;
}>();

const emit = defineEmits<{
  (e: 'bet'): void
  (e: 'favorite'): void
  (e: 'remove'): void
}>();

const isFavoritedProp = computed(() => {
  return typeof props.isFavorited === 'function' ? props.isFavorited(props.lottery) : false;
});
const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

const updateCountdown = () => {
  if (!props.lottery?.cutoff) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }
  const now = new Date();
  const cutoffRaw = props.lottery.cutoff as string | Date;
  const cutoffTime = typeof cutoffRaw === 'string'
    ? new Date(cutoffRaw.replace(' ', 'T'))
    : new Date(cutoffRaw);
  const diff = cutoffTime.getTime() - now.getTime();

  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  };
};

let timer: number;

// --- MOBILE DETECTION LOGIC ---
const isMobile = ref(window.matchMedia('(max-width: 768px)').matches);
let mediaQueryList: MediaQueryList;
const handleResize = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
};
// --- END MOBILE DETECTION LOGIC ---

onMounted(() => {
  updateCountdown();
  timer = window.setInterval(updateCountdown, 1000);

  mediaQueryList = window.matchMedia('(max-width: 768px)');
  mediaQueryList.addEventListener('change', handleResize);
  handleResize();
});
onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
  if (mediaQueryList && handleResize) {
    mediaQueryList.removeEventListener('change', handleResize);
  }
});

const toggleFavorite = (event: MouseEvent) => {
  event.stopPropagation();
  emit('favorite');
};

const navigateToDetail = () => {
  if (props.lottery && props.lottery.game_slug) {
    router.push(`/lottery/${props.lottery.game_slug}`);
  }
};

const handleCardClick = (event: MouseEvent) => {
  // Only navigate if the click is on the card background (not on interactive children)
  // This is now a fallback for keyboard accessibility (e.g. Enter/Space on the card)
  if (event.target === event.currentTarget) {
    navigateToDetail();
  }
};

const handleBet = () => {
  emit('bet');
};

const formattedDrawTime = computed(() => {
  if (!props.lottery?.cutoff) return '';
  const cutoffRaw = props.lottery.cutoff as string | Date;
  const date = typeof cutoffRaw === 'string'
    ? new Date(cutoffRaw.replace(' ', 'T'))
    : new Date(cutoffRaw);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year}, ${hours}:${minutes}`;
});

onMounted(() => {
  mediaQueryList = window.matchMedia('(max-width: 768px)');
  mediaQueryList.addEventListener('change', handleResize);
  handleResize();
});
onUnmounted(() => {
  mediaQueryList.removeEventListener('change', handleResize);
});

const betClosed = ref(false);

watch(
  () => [countdown.value.hours, countdown.value.minutes, countdown.value.seconds],
  ([hours, minutes, seconds]) => {
    if (hours === 0 && minutes === 0 && seconds === 0 && !betClosed.value) {
      betClosed.value = true;
      setTimeout(() => {
        emit('remove');
      }, 3000);
    }
  }
);
</script>

<style scoped>
.lottery-card {
  background: var(--bg-dark-tertiary);
  overflow: hidden;
  transition: all 0.2s;
  position: relative;
  width: 254.18px;
  height: 403.55px;
  color: var(--text-primary);
  border: 1px solid var(--text-primary);
}

.draw-card {
  /* Two-tone background: top half white, bottom half dark tertiary */
  background: var(--draw-card-gradient, linear-gradient(to bottom, var(--bg-secondary) 35%, var(--lottery-card-gradient) 22%));
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 254.18px;
  height: 403.55px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

@media (max-width: 768px) {
  .draw-card {
    width: 101.4px;
    height: 161px;
    padding: 6px;
    gap: 1px;
    display: flex;
    flex-direction: column;
  }

  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .countdown {
    margin-top: auto;
    padding: 1px;
  }
}

.draw-card.horizontal {
  width: 100%;
  max-width: 100%;
  height: 104.80255126953125px;
  padding: 0;
  position: relative;
  background: none;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .draw-card.horizontal {
    width: 100%;
    max-width: 100%;
    height: 44.88907241821289px;
    opacity: 1;
  }
}


.card-content {
  display: flex;
  flex-direction: column;
  /* gap: 14px; */
  flex: 1;
}

.lottery-name {
  color: var(--text-primary);
  text-overflow: ellipsis;
}

.horizontal .lottery-name {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  color: var(--text-primary);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .horizontal .lottery-name {
    font-size: 12.38px;
    font-weight: 500;
    line-height: 1.2;
    display: flex;
    align-items: center;
    height: 100%;
  }
}

.horizontal .card-content {
  display: flex;
  flex-direction: row;
  height: 104.80255126953125px;
  gap: 0;
  width: 100%;
  max-width: 100%;
  position: relative;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .horizontal .card-content {
    height: 44.88907241821289px;
    position: relative;
  }
}


.horizontal .left-section {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--bg-left-section, var(--bg-secondary));
  padding: 8px 8px 8px 36px;
  flex: 1;
  position: relative;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
}

@media (max-width: 768px) {
  .horizontal .left-section {
    padding: 0 4px 0 16px;
    gap: 8px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

.horizontal .right-section {
  background: var(--bg-right-section, var(--lottery-card-gradient));
  padding: 16px 16px;
  display: flex;
  align-items: center;
  margin-left: 0;
  flex-shrink: 0;
  min-width: 140px;
  max-width: 180px;
  width: auto;
  cursor: pointer;
}

@media (max-width: 768px) {
  .horizontal .right-section {
    padding: 4px 8px;
    min-width: 70px;
    height: 100%;
  }
}


.horizontal .flag-container {
  padding: 0;
  margin: 0;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .horizontal .flag-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.horizontal .flag {
  width: 67.45911407470703px;
  height: 67.45911407470703px;
  border: 3.61px solid var(--bg-dark-card, var(--lottery-card-gradient));
  border-radius: 50%;
  padding: 0;
}

@media (max-width: 768px) {
  .horizontal .flag {
    width: 28.894115447998047px !important;
    height: 28.894115447998047px !important;
    border: 1.55px solid var(--bg-dark-card, var(--lottery-card-gradient)) !important;
    opacity: 1 !important;
  }
}


.horizontal .country-name {
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.horizontal .game-type {
  opacity: 0.7;
  flex-shrink: 0;
}

.horizontal .favorite-btn {
  position: absolute;
  top: 0px;
  right: 0px;
  color: var(--text-light);
  font-size: 24px;
  width: 32px;
  height: 32px;
  z-index: 10;
}

.horizontal-favorite {
  position: absolute;
  top: 4px;
  left: 4px;
  color: var(--text-light);
  font-size: 18px;
  width: 20px;
  height: 20px;
  z-index: 10;
}

@media (max-width: 768px) {
  .horizontal-favorite {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 24px;
    height: 24px;
    font-size: 18px;
    z-index: 10;
    padding: 2px;
  }

  .horizontal .favorite-btn {
    position: absolute;
    top: -11px;
    right: 0px;
    color: var(--text-light);
    font-size: 24px;
    width: 32px;
    height: 32px;
    z-index: 10;
  }

  @media (max-width: 768px) {
    .horizontal .favorite-btn {
      top: 3px;
      right: 6.63px;
      width: 16px;
      height: 16px;
      font-size: 14px;
    }
  }

  
  .horizontal-favorite .favorite-icon {
    width: 12px;
    height: 12px;
    opacity: 1;
  }
}

.horizontal .countdown {
  padding: 0;
  margin: 0;
  gap: 12px;
}

@media (max-width: 768px) {
  .horizontal .countdown {
    gap: 6px;
  }
}

.horizontal .time {
  font-size: 28.91px;
  font-weight: 500;
  color: var(--text-countdown-digit, #ffffff);
}

@media (max-width: 768px) {
  .horizontal .time {
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
  }
}

.horizontal .label {
  font-size: 14px;
  letter-spacing: 0;
  color: var(--text-countdown-label-on-band, rgba(255, 255, 255, 0.92));
}

@media (max-width: 768px) {
  .horizontal .label {
    font-size: 7px;
    line-height: 1;
  }
}


.horizontal .bet-button {
  display: none;
}

.draw-card::after {
  display: none;
}

.card-header {
  /* display: flex;
  justify-content: space-between;
  align-items: center; */
  /* min-height: 36px; */
  position: relative;
  z-index: 2;
}

@media (max-width: 768px) {
  .card-header {
    flex: 1;
    gap: 1px;
  }
}

.horizontal .card-header {
  flex: 1;
}

.country-name {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 44px);
}

@media (max-width: 768px) {
  .country-name {
    font-size: 12.38px;
    margin: 0 0 0 1px;
    line-height: 1.1;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .card-header {
    gap: 1px;
  }
}

.favorite-btn {
  background: none;
  border: none;
  color: var(--text-light);
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.favorite-icon-wrapper {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
}
.favorite-icon-wrapper img {
  filter: var(--favorite-icon-filter, none);
}

.active-favorite-icon {
  filter: var(--favorite-icon-active-filter, none);
}
.favorite-icon {
  width: 16px;
  height: 16px;
  transition: all 0.2s ease;
  /* Use theme-based filter for default state */
  filter: var(--favorite-icon-filter, none);
  object-fit: contain;
}

@media (max-width: 768px) {
  .favorite-btn {
    width: 20px;
    height: 20px;
    font-size: 18px;
  }
  
  .favorite-icon-wrapper {
    width: 12px;
    height: 12px;
  }
  
  .favorite-icon {
    width: 12px;
    height: 12px;
  }
}

.favorite-btn:hover .favorite-icon {
  filter: var(--favorite-icon-hover-filter, brightness(0.7));
  transform: scale(1.1);
}

.favorite-btn.active .favorite-icon {
  /* Use theme color for active state - red filter */
  filter: var(--favorite-icon-active-filter, brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(1000%) hue-rotate(0deg) brightness(100%) contrast(100%));
}

.flag-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 12px 0;
  position: relative;
  z-index: 2;
}

@media (max-width: 768px) {
  .flag-container {
    padding: 0;
    margin: 0;
  }
}

.horizontal .flag-container {
  flex: 0 0 auto;
  padding: 0;
}

.flag {
  width: 132.50897216796875px;
  height: 132.50897216796875px;
  object-fit: contain;
  border: 14.46px solid var(--bg-dark-card, var(--lottery-card-gradient));
  border-radius: 50%;
  padding: 0;
}

@media (max-width: 768px) {
  .flag {
    width: 40px;
    height: 40px;
    border: 4px solid var(--bg-dark-card, var(--lottery-card-gradient));
  }
}

.horizontal .flag {
  width: 64px;
  height: 64px;
}

.countdown {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  position: relative;
  z-index: 2;
  border-radius: 8px;
  margin: 0 -8px;
}

@media (max-width: 768px) {
  .countdown {
    padding: 4px;
    margin: 0 -2px;
  }
}

.horizontal .countdown {
  padding: 0;
  margin: 0;
  justify-content: flex-start;
  flex: 0 0 auto;
}


.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

@media (max-width: 768px) {
  .time-block {
    gap: 1px;
  }
}

.time {
  font-size: 24px;
  font-weight: bold;
  color: var(--text-countdown-digit, #ffffff);
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .time {
    font-size: 12px;
    margin-bottom: 1px;
  }
}

.label {
  font-size: 10px;
  color: var(--text-countdown-label-on-band, rgba(255, 255, 255, 0.92));
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .label {
    font-size: 7px;
  }
}

.horizontal .label {
  font-size: 12px;
}

.horizontal .bet-button {
  margin: 0;
  width: auto;
  height: auto;
  padding: 12px 24px;
  white-space: nowrap;
  border-radius: 4px;
  flex: 0 0 auto;
  min-width: 120px;
}

.bet-button {
  z-index: 2;
  background: var(--bg-button);
  border: none;
  color: var(--text-button , var(--text-white));
  font-size: 16px;
  font-weight: 600;
  height: 62.64px;
  cursor: pointer;
  transition: background-color 0.2s;
  text-transform: uppercase;
  margin: 0 -14px -14px -14px;
  width: calc(100% + 28px);
}

@media (max-width: 768px) {
  .bet-button {
    height: 20px;
    font-size: 10px;
    margin: 2px -6px -6px -6px;
    width: calc(100% + 12px);
  }
}

.bet-button:hover {
  background: var(--bg-button-hover);
}

.horizontal .card-header {
  flex: 1;
  justify-content: flex-start;
}

.horizontal .country-name {
  max-width: 100%;
}
</style> 
